// src/modules/obras/services/obrasFS.js
//
// Servicio Firestore para la colección `obras` (Obras PVC).
// El progreso de la obra se deriva de HECHOS (documentos adjuntos + marcas de
// entrega/balance) — ver estadosObra.js. Cada hecho queda registrado en
// `historial` con quién y cuándo, reemplazando los avisos por WhatsApp.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  writeBatch,
  increment,
  serverTimestamp,
  Timestamp,
  deleteField,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from "firebase/storage";
import { db, storage } from "../../../firebase";
import { DOCS_OBRA } from "./estadosObra";
import { extraerObraNumero } from "../../pedidos/services/parseDescripcionPedido";

export function obraDocId(numero) {
  return `OBRA - ${String(numero).trim()}`;
}

/**
 * ID de documento Firestore seguro: los IDs no admiten "/" (los toma como
 * subcolección). Ej: código "BANDEJA P/PINTURA" → "BANDEJA P_PINTURA".
 * El valor real siempre se guarda como campo dentro del doc.
 */
function idSeguro(s) {
  return String(s).trim().replace(/\//g, "_");
}

function entradaHistorial(evento, user, extra = {}) {
  return {
    evento,
    at: Timestamp.now(), // serverTimestamp() no se admite dentro de arrayUnion
    porUid: user?.uid || null,
    porNombre: user?.nombre || user?.displayName || user?.email || null,
    auto: !!extra.auto,
    nota: extra.nota || null,
  };
}

function firma(user) {
  return {
    at: Timestamp.now(),
    porUid: user?.uid || null,
    porNombre: user?.nombre || user?.displayName || user?.email || null,
  };
}

/**
 * Crea una obra nueva. Falla si ya existe una obra con ese número.
 */
export async function crearObra(datos, user) {
  const numero = Number(String(datos.numero).trim());
  if (!Number.isInteger(numero) || numero <= 0) {
    throw new Error("El número de obra debe ser un número válido");
  }

  const id = obraDocId(numero);
  const ref = doc(db, "obras", id);
  const snap = await getDoc(ref);
  if (snap.exists()) throw new Error(`Ya existe la obra ${numero}`);

  // Red de seguridad: si el número se reusa, borrar subcolecciones huérfanas de
  // una obra anterior (cobranzas/facturas que apuntarían a archivos ya borrados).
  await borrarSubcoleccionesObra(id);

  await setDoc(ref, {
    id,
    numero,
    cliente: (datos.cliente || "").trim(),
    clienteCodigo: datos.clienteCodigo || null,
    nombreObra: (datos.nombreObra || "").trim(),
    // Monto del presupuesto (lo que paga el cliente, leído del presupuesto OP)
    montoPresupuesto: Number.isFinite(datos.montoPresupuesto) ? datos.montoPresupuesto : null,
    monedaPresupuesto: datos.monedaPresupuesto || null,
    carpintero: datos.carpintero || null,
    notas: (datos.notas || "").trim(),
    pedidoId: null,
    // Pedido de Finnegans elegido al crear — se vincula recién cuando se
    // adjunta el pedido OP (requisito del proceso)
    pedidoSugerido: datos.pedidoSugerido
      ? {
          id: datos.pedidoSugerido.id,
          cliente: datos.pedidoSugerido.cliente || null,
          obraNumero: datos.pedidoSugerido.obraNumero || null,
        }
      : null,
    // Legajo de la obra: cada documento es { nombre, url, path, at, porUid, porNombre }
    archivos: { op: null, despacho: null, factura: null, cobro: null },
    // Hitos manuales: { at, porUid, porNombre } cuando se marcan
    entregada: null,
    balanceHecho: null,
    historial: [entradaHistorial("CREADA", user)],
    creadoAt: serverTimestamp(),
    creadoPor: user?.uid || null,
    creadoPorNombre: user?.nombre || user?.displayName || user?.email || null,
    updatedAt: serverTimestamp(),
  });

  return { id };
}

/** Escucha todas las obras (el volumen es bajo — se ordena en el cliente). */
export function listenObras(onChange, onError) {
  const col = collection(db, "obras");
  return onSnapshot(
    col,
    (snap) => {
      const obras = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      obras.sort((a, b) => (b.numero || 0) - (a.numero || 0));
      onChange(obras);
    },
    (err) => {
      console.error("[listenObras]", err);
      onError?.(err);
    }
  );
}

/** Escucha una obra puntual. */
export function listenObra(obraId, onChange, onError) {
  return onSnapshot(
    doc(db, "obras", obraId),
    (snap) => onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (err) => {
      console.error("[listenObra]", err);
      onError?.(err);
    }
  );
}

/**
 * Marca / desmarca la entrega de la obra (Ceci, cuando se instala).
 */
export async function marcarEntregada(obraId, valor, user) {
  await updateDoc(doc(db, "obras", obraId), {
    entregada: valor ? firma(user) : null,
    historial: arrayUnion(entradaHistorial(valor ? "ENTREGADA" : "ENTREGA_DESHECHA", user)),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Marca / desmarca el balance como hecho (Judy/Andrés). Cuando exista la
 * pantalla de balance (fase 2), esta marca la va a poner el balance guardado.
 */
export async function marcarBalance(obraId, valor, user) {
  await updateDoc(doc(db, "obras", obraId), {
    balanceHecho: valor ? firma(user) : null,
    historial: arrayUnion(entradaHistorial(valor ? "BALANCE_HECHO" : "BALANCE_DESHECHO", user)),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Sube un documento del legajo de la obra (op | despacho | factura | cobro)
 * a Storage y lo registra en `archivos.<tipo>`. Cada documento es requisito
 * para avanzar en el estado correspondiente (ver DOC_REQUERIDO_PARA).
 */
export async function subirArchivoObra(obraId, tipo, file, user) {
  if (!DOCS_OBRA.some(d => d.tipo === tipo)) throw new Error(`Tipo de documento inválido: ${tipo}`);
  if (!file) throw new Error("No se seleccionó ningún archivo");
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/${tipo}-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);

  // El SDK de Storage reintenta errores de red indefinidamente — con un
  // timeout devolvemos un error claro en vez de un "Subiendo…" eterno.
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("La subida está tardando demasiado. Revisá la conexión y volvé a intentar.")), 60000)
  );
  await Promise.race([uploadBytes(r, file), timeout]);
  const url = await getDownloadURL(r);

  // Al REEMPLAZAR el OP con el proceso ya arrancado (pedido vinculado), guardamos
  // una foto del OP anterior para poder calcular qué hay que pedir de más (el
  // blob viejo sigue en Storage, en su path anterior). Dispara la revisión de
  // cambios en la ficha (opCambioPendiente).
  let cambioOP = {};
  if (tipo === "op") {
    try {
      const snap = await getDoc(doc(db, "obras", obraId));
      const data = snap.exists() ? snap.data() : {};
      const opPrevio = data?.archivos?.op || null;
      if (opPrevio && data?.pedidoId) {
        cambioOP = { opAnterior: opPrevio, opCambioPendiente: true };
      }
    } catch (e) { console.warn("[subirArchivoObra] snapshot OP anterior", e); }
  }

  await updateDoc(doc(db, "obras", obraId), {
    [`archivos.${tipo}`]: {
      nombre: file.name,
      url,
      path,
      ...firma(user),
    },
    ...cambioOP,
    // Al reemplazar el OP se pierde la confirmación de lectura anterior
    ...(tipo === "op" ? { opConfirmado: null } : {}),
    // Al reemplazar la recepción se pierde la aceptación anterior
    ...(tipo === "recepcionCompra" ? { recepcionAceptada: null } : {}),
    historial: arrayUnion(
      entradaHistorial(`DOC_${tipo.toUpperCase()}`, user, { nota: file.name })
    ),
    updatedAt: serverTimestamp(),
  });
  return url;
}

// ─── Pedidos de OP (la producción puede hacerse en ETAPAS) ──────────────────
// obra.ops = [{ id, archivo, carpintero, at, por }]. Los productos de TODOS los
// OP se suman para armar los dos pedidos de Finnegans (R8 y GUALE), igual que
// una obra normal. Al cambiar la lista se pierde la confirmación de lectura.

/** Migra una obra vieja (archivos.op único) al array `ops`, si hace falta. */
function opsActuales(data) {
  if (Array.isArray(data?.ops) && data.ops.length > 0) return data.ops;
  const legacy = data?.archivos?.op || data?.archivoOP || null;
  return legacy
    ? [{ id: `legacy-${Date.now()}`, archivo: legacy, carpintero: data?.carpintero || null, at: Timestamp.now(), por: null }]
    : [];
}

export async function agregarOP(obraId, file, carpintero, user) {
  if (!file) throw new Error("No se seleccionó ningún archivo");
  const ext = (file.name.split(".").pop() || "doc").toLowerCase();
  const path = `obras/${obraId}/op-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const archivo = { nombre: file.name, url: await getDownloadURL(r), path };

  const snap = await getDoc(doc(db, "obras", obraId));
  const data = snap.exists() ? snap.data() : {};
  const ops = [...opsActuales(data), {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    archivo,
    carpintero: carpintero || null,
    at: Timestamp.now(),
    por: user?.nombre || user?.displayName || user?.email || null,
  }];

  await updateDoc(doc(db, "obras", obraId), {
    ops,
    opConfirmado: null, // cambió el conjunto de OPs: hay que revisar la lectura
    historial: arrayUnion(entradaHistorial("OP_AGREGADO", user, {
      nota: `${file.name}${carpintero ? ` · ${carpintero}` : ""}`,
    })),
    updatedAt: serverTimestamp(),
  });
}

export async function eliminarOP(obra, opId, user) {
  const ops = opsActuales(obra);
  const quitado = ops.find(o => o.id === opId);
  if (quitado?.archivo?.path) {
    try { await deleteObject(storageRef(storage, quitado.archivo.path)); }
    catch (e) { console.warn("[eliminarOP] storage", e); }
  }
  await updateDoc(doc(db, "obras", obra.id), {
    ops: ops.filter(o => o.id !== opId),
    archivos: { ...(obra.archivos || {}), op: null }, // corta el legacy
    opConfirmado: null,
    historial: arrayUnion(entradaHistorial("OP_QUITADO", user || {}, { nota: quitado?.archivo?.nombre || opId })),
    updatedAt: serverTimestamp(),
  });
}

/** Asigna el carpintero responsable de una etapa (un OP). */
export async function setCarpinteroOP(obra, opId, carpintero, user) {
  const ops = opsActuales(obra).map(o => o.id === opId ? { ...o, carpintero: carpintero || null } : o);
  await updateDoc(doc(db, "obras", obra.id), {
    ops,
    historial: arrayUnion(entradaHistorial("OP_CARPINTERO", user || {}, { nota: `${carpintero || "sin asignar"}` })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Elimina un archivo adjunto de la obra (presupuesto, op, despacho, etc.) — por
 * si se adjuntó el equivocado. Borra el blob de Storage (best-effort), quita el
 * campo archivos.{tipo} y resetea las marcas dependientes (confirmación del OP,
 * aceptación de la recepción).
 */
export async function eliminarArchivoObra(obraId, tipo, user, path) {
  if (path) {
    try { await deleteObject(storageRef(storage, path)); }
    catch (e) { console.warn("[eliminarArchivoObra] storage", e); }
  }
  await updateDoc(doc(db, "obras", obraId), {
    [`archivos.${tipo}`]: deleteField(),
    ...(tipo === "op" ? { opConfirmado: null } : {}),
    ...(tipo === "recepcionCompra" ? { recepcionAceptada: null } : {}),
    historial: arrayUnion(entradaHistorial(`DOC_${tipo.toUpperCase()}_ELIMINADO`, user, {})),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Adjunta un remito de despacho ADICIONAL (ampliación / segunda ronda) sin tocar
 * el despacho principal ni su verificación. destino: "r8"|"guale". Se guardan en
 * el array despachosExtra (cada uno con id propio para poder eliminarlo).
 */
export async function agregarDespachoExtra(obraId, destino, file, user) {
  if (!file) throw new Error("No se seleccionó ningún archivo");
  const dest = destino === "guale" ? "guale" : "r8";
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/despacho-${dest}-extra-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const archivo = { nombre: file.name, url: await getDownloadURL(r), path };
  const entrada = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    destino: dest,
    archivo,
    at: Timestamp.now(),
    por: user?.nombre || user?.displayName || user?.email || null,
  };
  await updateDoc(doc(db, "obras", obraId), {
    despachosExtra: arrayUnion(entrada),
    historial: arrayUnion(entradaHistorial("DESPACHO_EXTRA", user, { nota: `${dest.toUpperCase()} · ${file.name}` })),
    updatedAt: serverTimestamp(),
  });
  return entrada.id;
}

/** Identifica a qué etapa (carpintero) corresponde un remito adicional. */
export async function setCarpinteroDespachoExtra(obra, despachoId, carpintero, user) {
  const lista = (obra.despachosExtra || []).map(d =>
    d.id === despachoId ? { ...d, carpintero: carpintero || null } : d
  );
  await updateDoc(doc(db, "obras", obra.id), {
    despachosExtra: lista,
    historial: arrayUnion(entradaHistorial("DESPACHO_EXTRA_CARPINTERO", user || {}, { nota: carpintero || "sin asignar" })),
    updatedAt: serverTimestamp(),
  });
}

export async function eliminarDespachoExtra(obra, despachoId, user) {
  const restantes = (obra.despachosExtra || []).filter(d => d.id !== despachoId);
  await updateDoc(doc(db, "obras", obra.id), {
    despachosExtra: restantes,
    historial: arrayUnion(entradaHistorial("DESPACHO_EXTRA_QUITADO", user || {}, {})),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Actualiza el monto del presupuesto (al adjuntar/reemplazar el presupuesto).
 * Registra el cambio en el historial para dejar trazado la modificación.
 */
export async function actualizarMontoPresupuesto(obraId, { monto, moneda }, user, nota) {
  await updateDoc(doc(db, "obras", obraId), {
    montoPresupuesto: Number.isFinite(monto) ? monto : null,
    monedaPresupuesto: moneda || null,
    presupuestoActualizadoAt: Timestamp.now(),
    historial: arrayUnion(entradaHistorial("PRESUPUESTO", user, { nota: nota || `Monto: ${moneda || ""} ${monto}` })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Admin: marca un hito como "cargado (pendiente de archivo)" o quita la marca.
 * Sirve para dar de alta obras ya avanzadas sin tener todavía el documento; el
 * paso avanza igual y queda señalado como pendiente de subir el archivo real.
 */
export async function marcarHitoManual(obraId, hito, valor, user) {
  await updateDoc(doc(db, "obras", obraId), {
    [`hitosManual.${hito}`]: valor ? { ...firma(user), pendiente: true } : deleteField(),
    historial: arrayUnion(entradaHistorial(valor ? "HITO_MANUAL" : "HITO_MANUAL_QUITADO", user, { nota: hito })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Marca como revisado el cambio de OP (limpia opCambioPendiente). Se usa cuando
 * ya se generó el pedido complementario o cuando el cambio no requiere pedido
 * nuevo (solo bajaron cantidades). Deja el OP anterior guardado como referencia.
 */
export async function descartarCambioOP(obraId, user, nota) {
  await updateDoc(doc(db, "obras", obraId), {
    opCambioPendiente: false,
    historial: arrayUnion(entradaHistorial("OP_CAMBIO_REVISADO", user, { nota: nota || "Cambio de OP revisado" })),
    updatedAt: serverTimestamp(),
  });
}

// ─── Cobranzas de la obra (pagos del cliente) ───────────────────────────────
// Subcolección obras/{id}/cobranzas — se guarda en `balance` para respetar las
// reglas ya existentes (solo finanzas). Cada pago: monto, fecha, comprobante
// (Ceci) y cobranza de Finnegans (Marcela).

export function listenCobranzas(obraId, onChange, onError) {
  return onSnapshot(
    collection(db, "obras", obraId, "cobranzas"),
    (snap) => {
      const cobranzas = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.fecha?.seconds || a.at?.seconds || 0) - (b.fecha?.seconds || b.at?.seconds || 0));
      onChange(cobranzas);
    },
    (err) => { console.warn("[listenCobranzas]", err); onError?.(err); }
  );
}

export async function agregarCobranza(obraId, { monto, fecha, nota, efectivo }, user) {
  const valor = Number(monto);
  if (!Number.isFinite(valor) || valor <= 0) throw new Error("Ingresá un monto de cobranza válido");
  const ref = await addDoc(collection(db, "obras", obraId, "cobranzas"), {
    monto: valor,
    fecha: fecha ? Timestamp.fromDate(new Date(`${fecha}T00:00:00`)) : Timestamp.now(),
    nota: nota?.trim() || null,
    efectivo: !!efectivo, // en efectivo no hay comprobante de pago para adjuntar
    comprobante: null,
    finnegans: null,
    ...firma(user),
  });
  await recalcularMontoCobrado(obraId);
  return ref.id;
}

/**
 * Recalcula el rollup `montoCobrado` sumando las cobranzas REALES. Se usa en vez
 * de increment/decrement, que puede desincronizarse (ej. al borrar cobranzas
 * huérfanas de una obra reusada, restaba montos nunca sumados → total negativo).
 */
export async function recalcularMontoCobrado(obraId) {
  const snap = await getDocs(collection(db, "obras", obraId, "cobranzas"));
  let total = 0, efectivo = 0, noEfectivo = 0;
  for (const d of snap.docs) {
    const c = d.data();
    const m = Number(c.monto) || 0;
    total += m;
    if (c.efectivo) efectivo += m; else noEfectivo += m;
  }
  const r = (v) => Math.round(v * 100) / 100;
  await updateDoc(doc(db, "obras", obraId), {
    montoCobrado: r(total),
    // Split por método → define cuánto factura cada vía:
    //   no efectivo (transferencia/cheque…) → Finnegans (Marcela)
    //   efectivo                            → Aéreo (Judy)
    montoCobradoNoEfectivo: r(noEfectivo),
    montoCobradoEfectivo: r(efectivo),
    updatedAt: serverTimestamp(),
  });
  return total;
}

/** Adjunta el comprobante de pago (Ceci) o la cobranza de Finnegans (Marcela). */
export async function adjuntarDocCobranza(obraId, cobranzaId, campo, file, user) {
  if (!["comprobante", "finnegans"].includes(campo)) throw new Error("Campo inválido");
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/cobranza-${cobranzaId}-${campo}-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  await updateDoc(doc(db, "obras", obraId, "cobranzas", cobranzaId), {
    [campo]: { nombre: file.name, url, path, ...firma(user) },
  });
  return url;
}

/** Edita un pago ya cargado (monto, fecha, nota, efectivo). Recalcula el total. */
export async function editarCobranza(obraId, cobranzaId, { monto, fecha, nota, efectivo }, user) {
  const valor = Number(monto);
  if (!Number.isFinite(valor) || valor <= 0) throw new Error("Ingresá un monto de cobranza válido");
  const patch = {
    monto: valor,
    nota: nota?.trim() || null,
    efectivo: !!efectivo,
    editadoAt: Timestamp.now(),
    editadoPor: user?.nombre || user?.displayName || user?.email || null,
  };
  if (fecha) patch.fecha = Timestamp.fromDate(new Date(`${fecha}T00:00:00`));
  await updateDoc(doc(db, "obras", obraId, "cobranzas", cobranzaId), patch);
  await recalcularMontoCobrado(obraId);
}

export async function eliminarCobranza(obraId, cobranzaId /* , monto */) {
  await deleteDoc(doc(db, "obras", obraId, "cobranzas", cobranzaId));
  await recalcularMontoCobrado(obraId);
}

// ─── Compra de mercadería en plaza (accesorios) ──────────────────────────────
// Subcolección obras/{id}/comprasPlaza — pueden ser VARIOS comprobantes porque
// los accesorios se compran en distintos lugares. Ceci y Marcela pueden cargar.
// Rollup comprasPlazaCount en el doc de la obra para derivar el hito facturaCompra.

export function listenComprasPlaza(obraId, onChange, onError) {
  return onSnapshot(
    collection(db, "obras", obraId, "comprasPlaza"),
    (snap) => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.at?.seconds || 0) - (b.at?.seconds || 0))),
    (err) => { console.warn("[listenComprasPlaza]", err); onError?.(err); }
  );
}

export async function agregarCompraPlaza(obraId, { nota, monto }, file, user) {
  if (!file) throw new Error("Adjuntá el comprobante de la compra");
  const valor = Number(monto);
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/compra-plaza-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const archivo = { nombre: file.name, url: await getDownloadURL(r), path };

  const ref = await addDoc(collection(db, "obras", obraId, "comprasPlaza"), {
    nota: nota || null,
    monto: Number.isFinite(valor) && valor > 0 ? valor : null,
    archivo,
    ...firma(user),
  });
  await updateDoc(doc(db, "obras", obraId), {
    comprasPlazaCount: increment(1),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function eliminarCompraPlaza(obraId, compraId) {
  await deleteDoc(doc(db, "obras", obraId, "comprasPlaza", compraId));
  await updateDoc(doc(db, "obras", obraId), {
    comprasPlazaCount: increment(-1),
    updatedAt: serverTimestamp(),
  });
}

// ─── Facturas de venta (pueden ser varias; una parte puede ir "en negro") ────
// Subcolección obras/{id}/facturas — cada factura con su monto NETO (sin IVA).
// El total facturado (rollup en obra.montoFacturado) reemplaza al presupuesto
// como "precio de venta" del balance.

export function listenFacturas(obraId, onChange, onError) {
  return onSnapshot(
    collection(db, "obras", obraId, "facturas"),
    (snap) => onChange(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.at?.seconds || 0) - (b.at?.seconds || 0))),
    (err) => { console.warn("[listenFacturas]", err); onError?.(err); }
  );
}

export async function agregarFactura(obraId, { monto, nota, tipo }, file, user) {
  const valor = Number(monto);
  if (!Number.isFinite(valor) || valor <= 0) throw new Error("Ingresá el monto neto (sin IVA) de la factura");
  if (!file) throw new Error("Adjuntá el documento de Finnegans de la factura");
  const clase = tipo === "negro" ? "negro" : "blanco";
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/factura-${clase}-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const archivo = { nombre: file.name, url: await getDownloadURL(r), path };

  const ref = await addDoc(collection(db, "obras", obraId, "facturas"), {
    monto: valor, nota: nota || null, tipo: clase, archivo, ...firma(user),
  });
  await updateDoc(doc(db, "obras", obraId), {
    montoFacturado: increment(valor),
    [clase === "negro" ? "montoFacturadoNegro" : "montoFacturadoBlanco"]: increment(valor),
    facturasCount: increment(1),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function eliminarFactura(obraId, facturaId, monto, tipo) {
  await deleteDoc(doc(db, "obras", obraId, "facturas", facturaId));
  const clase = tipo === "negro" ? "montoFacturadoNegro" : "montoFacturadoBlanco";
  await updateDoc(doc(db, "obras", obraId), {
    montoFacturado: increment(-(Number(monto) || 0)),
    [clase]: increment(-(Number(monto) || 0)),
    facturasCount: increment(-1),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Plan de facturación (Judy/Andrés): cómo se reparte el valor a facturar entre
 * blanco (Marcela, factura Finnegans) y negro (Judy). Libre — puede superar el
 * presupuesto porque incluye gastos extra (instalación, mano de obra).
 */
/**
 * A nombre de quién se emite la factura de la obra (del maestro de clientes).
 * Los MONTOS a facturar ya no se definen a mano: se derivan de la cobranza y su
 * método (no efectivo → Finnegans/Marcela · efectivo → Aéreo/Judy).
 */
export async function setFacturaANombreDe(obraId, aNombreDe, user) {
  if (!aNombreDe?.nombre) throw new Error("Elegí a nombre de quién va la factura");
  await updateDoc(doc(db, "obras", obraId), {
    facturaANombreDe: { nombre: aNombreDe.nombre, codigo: aNombreDe.codigo || null, ...firma(user) },
    historial: arrayUnion(entradaHistorial("FACTURA_A_NOMBRE", user, { nota: aNombreDe.nombre })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Judy/Andrés habilitan facturar ANTES de terminar de cobrar (el cliente pagó
 * una parte y pide factura). Queda el motivo y la firma para la trazabilidad.
 */
export async function habilitarFacturacionAnticipada(obraId, motivo, user) {
  await updateDoc(doc(db, "obras", obraId), {
    facturacionAnticipada: { motivo: motivo || null, ...firma(user) },
    historial: arrayUnion(entradaHistorial("FACTURACION_ANTICIPADA", user, { nota: motivo || "sin motivo" })),
    updatedAt: serverTimestamp(),
  });
}

export async function quitarFacturacionAnticipada(obraId, user) {
  await updateDoc(doc(db, "obras", obraId), {
    facturacionAnticipada: deleteField(),
    historial: arrayUnion(entradaHistorial("FACTURACION_ANTICIPADA_QUITADA", user || {}, {})),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cecilia confirma que la lectura automática del OP coincide con el pedido real.
 * Guarda el resumen por categoría (para auditoría) y habilita el avance.
 */
export async function confirmarLecturaOP(obraId, resumenCategorias, user) {
  await updateDoc(doc(db, "obras", obraId), {
    opConfirmado: { ...firma(user), categorias: resumenCategorias || null },
    historial: arrayUnion(entradaHistorial("OP_CONFIRMADO", user, { nota: "Lectura del OP confirmada" })),
    updatedAt: serverTimestamp(),
  });
}

/** Deshace la confirmación (por si el OP se reemplaza o hay que revisar). */
export async function desconfirmarLecturaOP(obraId, user) {
  await updateDoc(doc(db, "obras", obraId), {
    opConfirmado: null,
    updatedAt: serverTimestamp(),
  });
}

/** Lee un documento del legajo con compatibilidad hacia atrás (archivoOP viejo). */
export function getArchivoObra(obra, tipo) {
  if (!obra) return null;
  const nuevo = obra.archivos?.[tipo] || null;
  if (nuevo) return nuevo;
  if (tipo === "op") return obra.archivoOP || null;
  return null;
}

/**
 * Elimina una obra por completo (solo admin — lo exigen las reglas).
 * Borra también los archivos adjuntos en Storage (best-effort).
 * Los pedidos de Finnegans vinculados NO se tocan.
 */
// Subcolecciones de una obra — hay que borrarlas a mano: en Firestore, borrar
// el documento NO borra sus subcolecciones (quedarían huérfanas y, si se reusa
// el número de obra, reaparecen apuntando a archivos ya inexistentes → 403).
const SUBCOLECCIONES_OBRA = ["cobranzas", "facturas", "comprasPlaza", "balance"];

/** Borra todos los docs de las subcolecciones de una obra. */
async function borrarSubcoleccionesObra(obraId) {
  for (const sc of SUBCOLECCIONES_OBRA) {
    try {
      const snap = await getDocs(collection(db, "obras", obraId, sc));
      await Promise.allSettled(snap.docs.map(d => deleteDoc(d.ref)));
    } catch (e) {
      console.warn(`[borrarSubcoleccionesObra] ${sc}`, e);
    }
  }
}

export async function eliminarObra(obraId) {
  try {
    const carpeta = storageRef(storage, `obras/${obraId}`);
    const { items } = await listAll(carpeta);
    await Promise.allSettled(items.map(it => deleteObject(it)));
  } catch (e) {
    console.warn("[eliminarObra] no se pudieron borrar adjuntos:", e);
  }
  await borrarSubcoleccionesObra(obraId);
  await deleteDoc(doc(db, "obras", obraId));
}

// ─── Gastos de la obra (subcolección balance — solo finanzas) ───────────────
// Andrés carga los gastos a medida que ocurren; alimentan el balance final.

export function listenGastosObra(obraId, onChange, onError) {
  return onSnapshot(
    collection(db, "obras", obraId, "balance"),
    (snap) => {
      const gastos = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(g => g.tipo === "gasto")
        .sort((a, b) => (a.at?.seconds || 0) - (b.at?.seconds || 0));
      onChange(gastos);
    },
    (err) => {
      console.warn("[listenGastosObra]", err);
      onError?.(err);
    }
  );
}

export async function agregarGastoObra(obraId, { concepto, monto }, user) {
  const valor = Number(monto);
  if (!concepto?.trim()) throw new Error("Ingresá el concepto del gasto");
  if (!Number.isFinite(valor) || valor <= 0) throw new Error("Ingresá un monto válido");
  await addDoc(collection(db, "obras", obraId, "balance"), {
    tipo: "gasto",
    concepto: concepto.trim(),
    monto: valor,
    ...firma(user),
  });
}

export async function eliminarGastoObra(obraId, gastoId) {
  await deleteDoc(doc(db, "obras", obraId, "balance", gastoId));
}

/**
 * Guarda el mapa completo de sustituciones intencionales de la obra
 * (producto de Finnegans que reemplaza a un producto del OP, ej. por stock).
 */
export async function setSustitucionesObra(obraId, sustituciones, user, nota) {
  await updateDoc(doc(db, "obras", obraId), {
    sustituciones,
    ...(nota ? { historial: arrayUnion(entradaHistorial("SUSTITUCION", user, { nota })) } : {}),
    updatedAt: serverTimestamp(),
  });
}

/** Marca que la recepción de mercadería fue aceptada (coincida o no con el OP). */
export async function aceptarRecepcion(obraId, resumen, user) {
  await updateDoc(doc(db, "obras", obraId), {
    recepcionAceptada: { ...firma(user), ...resumen },
    historial: arrayUnion(entradaHistorial("RECEPCION_ACEPTADA", user, {
      nota: resumen?.ok ? "Recepción coincide con el OP" : `Recepción con diferencia aceptada (${resumen?.cantDoc} vs ${resumen?.cantOP})`,
    })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Guarda el resumen de la última verificación automática de un documento
 * (OP / despacho R8 / despacho Guale vs pedidos) para mostrarla en la ficha.
 */
export async function guardarVerificacionObra(obraId, tipo, resumen) {
  await updateDoc(doc(db, "obras", obraId), {
    [`verificaciones.${tipo}`]: { ...resumen, at: Timestamp.now() },
    updatedAt: serverTimestamp(),
  });
}

/** Actualiza datos editables de la obra (carpintero, notas, cliente, nombreObra). */
export async function actualizarObra(obraId, cambios) {
  const permitidos = {};
  for (const k of ["carpintero", "notas", "cliente", "nombreObra"]) {
    if (k in cambios) permitidos[k] = cambios[k];
  }
  await updateDoc(doc(db, "obras", obraId), {
    ...permitidos,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Vincula un pedido de Finnegans a la obra (completa el paso 1 junto con el OP).
 */
export async function vincularPedidoAObra(obra, pedido, user, extra = {}) {
  const cambios = {
    pedidoId: pedido.id,
    pedidoNumero: pedido.numero || pedido.id,
    pedidoCliente: pedido.cliente || null,
    historial: arrayUnion(
      entradaHistorial("PEDIDO_VINCULADO", user, {
        auto: !!extra.auto,
        nota: extra.auto
          ? `Detectado automáticamente: ${pedido.id}`
          : `Vinculado a ${pedido.id}`,
      })
    ),
    updatedAt: serverTimestamp(),
  };
  if (!obra.cliente && pedido.cliente) cambios.cliente = pedido.cliente;

  await updateDoc(doc(db, "obras", obra.id), cambios);
}

/**
 * Vincula un pedido COMPLEMENTARIO (ampliación por cambio de presupuesto) como
 * pedido adicional de la obra, sin reemplazar el principal. destino: "r8"|"guale".
 * Se guardan en el array pedidosExtra; el cross-check los suma a su depósito.
 */
export async function vincularPedidoComplementario(obra, pedido, destino, user) {
  const dest = destino === "guale" ? "guale" : "r8";
  const entrada = {
    pedidoId: pedido.id,
    pedidoNumero: pedido.numero || pedido.id,
    destino: dest,
    at: Timestamp.now(),
    por: user?.nombre || user?.displayName || user?.email || null,
  };
  await updateDoc(doc(db, "obras", obra.id), {
    pedidosExtra: arrayUnion(entrada),
    historial: arrayUnion(entradaHistorial("PEDIDO_COMPLEMENTARIO", user, { nota: `${dest.toUpperCase()} · ${pedido.id}` })),
    updatedAt: serverTimestamp(),
  });
}

export async function desvincularPedidoComplementario(obra, pedidoId, user) {
  const restantes = (obra.pedidosExtra || []).filter(p => p.pedidoId !== pedidoId);
  await updateDoc(doc(db, "obras", obra.id), {
    pedidosExtra: restantes,
    historial: arrayUnion(entradaHistorial("PEDIDO_COMPLEMENTARIO_QUITADO", user || {}, { nota: pedidoId })),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Admin: adjunta el PDF del pedido de Finnegans y guarda los productos LEÍDOS
 * del archivo (para obras viejas que no pasan por el sistema de pedidos). Actúa
 * como el pedido "vinculado" del depósito: alimenta el control cruzado contra el OP.
 * destino: "r8" | "guale". productos: [{ cod, desc, cant }] ya parseados.
 */
export async function adjuntarPedidoManual(obraId, destino, file, productos, user) {
  if (!file) throw new Error("No se seleccionó ningún archivo");
  const dest = destino === "guale" ? "guale" : "r8";
  const ext = (file.name.split(".").pop() || "pdf").toLowerCase();
  const path = `obras/${obraId}/pedido-manual-${dest}-${Date.now()}.${ext}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const archivo = { nombre: file.name, url: await getDownloadURL(r), path };
  const campo = dest === "guale" ? "pedidoAdjuntoGuale" : "pedidoAdjuntoR8";
  await updateDoc(doc(db, "obras", obraId), {
    [campo]: { archivo, productos: Array.isArray(productos) ? productos : [], ...firma(user) },
    historial: arrayUnion(entradaHistorial("PEDIDO_ADJUNTO", user, {
      nota: `${dest.toUpperCase()} · ${file.name} · ${productos?.length || 0} ítems leídos`,
    })),
    updatedAt: serverTimestamp(),
  });
}

export async function eliminarPedidoManual(obraId, destino, user, path) {
  const dest = destino === "guale" ? "guale" : "r8";
  if (path) {
    try { await deleteObject(storageRef(storage, path)); }
    catch (e) { console.warn("[eliminarPedidoManual] storage", e); }
  }
  const campo = dest === "guale" ? "pedidoAdjuntoGuale" : "pedidoAdjuntoR8";
  await updateDoc(doc(db, "obras", obraId), {
    [campo]: deleteField(),
    historial: arrayUnion(entradaHistorial("PEDIDO_ADJUNTO_QUITADO", user || {}, { nota: dest.toUpperCase() })),
    updatedAt: serverTimestamp(),
  });
}

/** Desvincula el pedido (por si se vinculó mal). */
export async function desvincularPedidoDeObra(obraId, user) {
  await updateDoc(doc(db, "obras", obraId), {
    pedidoId: null,
    pedidoNumero: null,
    pedidoCliente: null,
    historial: arrayUnion(entradaHistorial("PEDIDO_DESVINCULADO", user || {})),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Vincula el segundo pedido de la obra: la tela mosquitera que se despacha
 * desde Gualeguay (solo para obras que llevan tela).
 */
export async function vincularPedidoTelaAObra(obra, pedido, user) {
  await updateDoc(doc(db, "obras", obra.id), {
    pedidoTelaId: pedido.id,
    pedidoTelaNumero: pedido.numero || pedido.id,
    pedidoTelaCliente: pedido.cliente || null,
    historial: arrayUnion(
      entradaHistorial("PEDIDO_TELA_VINCULADO", user, { nota: `Vinculado a ${pedido.id} (tela — Gualeguay)` })
    ),
    updatedAt: serverTimestamp(),
  });
}

export async function desvincularPedidoTelaDeObra(obraId, user) {
  await updateDoc(doc(db, "obras", obraId), {
    pedidoTelaId: null,
    pedidoTelaNumero: null,
    pedidoTelaCliente: null,
    historial: arrayUnion(entradaHistorial("PEDIDO_TELA_DESVINCULADO", user || {})),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Pedidos activos de Finnegans que mencionan "Obra" en la descripción.
 * Parsea la descripción en el cliente (no depende de que el sync nuevo haya
 * grabado obraNumero), así detecta también pedidos sincronizados con código viejo.
 */
export async function getPedidosConObraDetectada() {
  const snap = await getDocs(collection(db, "pedidos"));
  const out = [];
  snap.docs.forEach(d => {
    const p = { id: d.id, ...d.data() };
    const num = Number(p.obraNumero) ||
      extraerObraNumero(String(p.descripcion || "").toUpperCase());
    if (num) out.push({ ...p, obraNumero: num });
  });
  return out.sort((a, b) => (b.obraNumero || 0) - (a.obraNumero || 0));
}

// ─── Costos de productos (para el balance) ──────────────────────────────────
// Colección obras_costos/{codigo}: { codigo, nombre, costo, precioVenta }.
// Solo visible para usuarios con obrasFinanzas (reglas de Firestore).

/**
 * Importa el Excel de costos de Finnegans (Costos_Prods_Finn.xlsx).
 * filas: [{ codigo, nombre, costo, precioVenta }]
 */
export async function importarCostosObras(filas, user, nombreArchivo) {
  const validas = filas.filter(f => f.codigo && Number.isFinite(f.costo));
  if (validas.length === 0) throw new Error("No se encontraron filas válidas en el archivo");

  let batch = writeBatch(db);
  let enBatch = 0;
  for (const f of validas) {
    batch.set(doc(db, "obras_costos", idSeguro(f.codigo)), {
      codigo: String(f.codigo).trim(),
      nombre: f.nombre || null,
      costo: f.costo,
      precioVenta: Number.isFinite(f.precioVenta) ? f.precioVenta : null,
      updatedAt: serverTimestamp(),
    });
    if (++enBatch === 450) { await batch.commit(); batch = writeBatch(db); enBatch = 0; }
  }
  batch.set(doc(db, "obras_costos", "_meta"), {
    actualizadoAt: serverTimestamp(),
    cantidad: validas.length,
    archivo: nombreArchivo || null,
    porUid: user?.uid || null,
    porNombre: user?.nombre || user?.displayName || user?.email || null,
  });
  await batch.commit();
  return { importados: validas.length };
}

/** Resumen del catálogo de costos (para mostrar en el importador). */
export async function getCostosResumen() {
  const meta = await getDoc(doc(db, "obras_costos", "_meta"));
  return meta.exists() ? meta.data() : null;
}

/** Mapa codigo → { costo, precioVenta, nombre } para valorizar el balance. */
export async function getCostosObras() {
  const snap = await getDocs(collection(db, "obras_costos"));
  const mapa = new Map();
  snap.docs.forEach(d => {
    if (d.id !== "_meta") mapa.set(d.data().codigo || d.id, d.data());
  });
  return mapa;
}

// ─── Maestro de productos Finnegans (para generar el import del pedido) ─────
// obras_productos/{codigo}: { codigo, nombre, activo, precio }
// obras_alias/{alias}: { codigo } — memoria de elecciones alias OP → producto

/** Importa el maestro de productos (Importacion de BSProducto*.xlsx). */
export async function importarProductosObras(filas, user, nombreArchivo) {
  const validas = filas.filter(f => f.codigo && f.nombre);
  if (validas.length === 0) throw new Error("No se encontraron productos válidos en el archivo");

  let batch = writeBatch(db);
  let enBatch = 0;
  for (const f of validas) {
    batch.set(doc(db, "obras_productos", idSeguro(f.codigo)), {
      codigo: String(f.codigo).trim(),
      nombre: f.nombre,
      activo: f.activo !== false,
      precio: Number.isFinite(f.precio) ? f.precio : null,
      updatedAt: serverTimestamp(),
    });
    if (++enBatch === 450) { await batch.commit(); batch = writeBatch(db); enBatch = 0; }
  }
  batch.set(doc(db, "obras_productos", "_meta"), {
    actualizadoAt: serverTimestamp(),
    cantidad: validas.length,
    archivo: nombreArchivo || null,
    porUid: user?.uid || null,
    porNombre: user?.nombre || user?.displayName || user?.email || null,
  });
  await batch.commit();
  return { importados: validas.length };
}

export async function getProductosResumen() {
  const meta = await getDoc(doc(db, "obras_productos", "_meta"));
  return meta.exists() ? meta.data() : null;
}

/** Lista completa del maestro (para resolver alias y armar el export). */
export async function getProductosObras() {
  const snap = await getDocs(collection(db, "obras_productos"));
  return snap.docs.filter(d => d.id !== "_meta").map(d => d.data());
}

/** Memoria de conversiones alias → código elegidas por el usuario. */
export async function getAliasGuardados() {
  const snap = await getDocs(collection(db, "obras_alias"));
  const mapa = new Map();
  snap.docs.forEach(d => mapa.set(d.data().alias || d.id, d.data().codigo));
  return mapa;
}

export async function guardarAlias(alias, codigo, user) {
  await setDoc(doc(db, "obras_alias", idSeguro(alias)), {
    alias,
    codigo: String(codigo),
    porNombre: user?.nombre || user?.displayName || user?.email || null,
    updatedAt: serverTimestamp(),
  });
}

/** Lista de clientes sincronizados (código + nombre) para el selector. */
export async function getClientesLista() {
  const snap = await getDocs(collection(db, "clientes"));
  return snap.docs
    .filter(d => d.id !== "_meta")
    .map(d => ({ codigo: d.data().codigo || d.id, nombre: d.data().nombre || "" }))
    .filter(c => c.nombre)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
}

/**
 * Importa la base de clientes desde un Excel (export de Finnegans). Solo admin
 * (reglas de la colección `clientes`). Usa `merge` para no pisar otros campos
 * que pueda tener el cliente (rut, listaPrecio) si el Excel no los trae.
 */
export async function importarClientesObras(filas, user, nombreArchivo) {
  const validas = filas.filter(f => f.codigo && f.nombre);
  if (validas.length === 0) throw new Error("No se encontraron clientes válidos en el archivo");

  let batch = writeBatch(db);
  let enBatch = 0;
  for (const f of validas) {
    const codigo = String(f.codigo).trim();
    batch.set(doc(db, "clientes", codigo), {
      codigo,
      nombre: f.nombre,
      ...(f.rut ? { rut: String(f.rut).trim() } : {}),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    if (++enBatch === 450) { await batch.commit(); batch = writeBatch(db); enBatch = 0; }
  }
  batch.set(doc(db, "clientes", "_meta"), {
    actualizadoAt: serverTimestamp(),
    cantidad: validas.length,
    archivo: nombreArchivo || null,
    porUid: user?.uid || null,
    porNombre: user?.nombre || user?.displayName || user?.email || null,
  }, { merge: true });
  await batch.commit();
  return { importados: validas.length };
}

/** Resumen de la última importación de clientes (para el importador). */
export async function getClientesResumen() {
  const meta = await getDoc(doc(db, "clientes", "_meta"));
  return meta.exists() ? meta.data() : null;
}

/** Guarda el cliente (código del maestro) en la obra. */
export async function setClienteObra(obraId, cliente, user) {
  await updateDoc(doc(db, "obras", obraId), {
    cliente: cliente.nombre || null,
    clienteCodigo: cliente.codigo || null,
    ...(user ? {
      historial: arrayUnion(entradaHistorial("CLIENTE_CAMBIADO", user, {
        nota: `${cliente.nombre || "—"}${cliente.codigo ? ` (${cliente.codigo})` : ""}`,
      })),
    } : {}),
    updatedAt: serverTimestamp(),
  });
}

/** Trae el pedido vinculado completo, esté activo, despachado o entregado. */
export async function getPedidoVinculado(pedidoId) {
  for (const col of ["pedidos", "pedidos_despachados", "pedidos_entregados"]) {
    const s = await getDoc(doc(db, col, pedidoId));
    if (s.exists()) return { id: s.id, ...s.data() };
  }
  return null;
}

/** IDs de pedidos que ya están vinculados a alguna obra del tablero. */
export async function getPedidoIdsVinculados() {
  const snap = await getDocs(collection(db, "obras"));
  return new Set(snap.docs.map(d => d.data().pedidoId).filter(Boolean));
}

/**
 * Escucha los pedidos (activos, despachados y entregados) cuya descripción
 * contiene el número de obra — el sync guarda `obraNumero` al parsear.
 * Llama a onChange con la lista combinada de coincidencias.
 */
export function listenPedidosPorObraNumero(numeroObra, onChange, onError) {
  const n = Number(numeroObra);
  if (!Number.isFinite(n)) return () => {};

  const resultados = { pedidos: [], pedidos_despachados: [], pedidos_entregados: [] };
  const emit = () => {
    onChange([
      ...resultados.pedidos.map(p => ({ ...p, _col: "pedidos" })),
      ...resultados.pedidos_despachados.map(p => ({ ...p, _col: "pedidos_despachados" })),
      ...resultados.pedidos_entregados.map(p => ({ ...p, _col: "pedidos_entregados" })),
    ]);
  };

  const unsubs = ["pedidos", "pedidos_despachados", "pedidos_entregados"].map(col =>
    onSnapshot(
      query(collection(db, col), where("obraNumero", "==", n)),
      (snap) => {
        resultados[col] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        emit();
      },
      (err) => {
        console.warn(`[listenPedidosPorObraNumero] ${col}:`, err);
        onError?.(err);
      }
    )
  );

  return () => unsubs.forEach(u => { try { u(); } catch {} });
}
